import { useEffect, useState } from "react";
import { adminSettings, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Gear } from "@phosphor-icons/react";
import AdminCard from "../components/AdminCard";
import AdminPrimaryButton from "../components/AdminPrimaryButton";
import AdminToggle from "../components/AdminToggle";

export default function SettingsTab({ onSaved }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [refreshInterval, setRefreshInterval] = useState(15);
  const [aiEnabled, setAiEnabled] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    let mounted = true;
    adminSettings.get()
      .then((s) => {
        if (!mounted) return;
        setData(s);
        setRefreshInterval(s.refresh_interval_minutes);
        setAiEnabled(s.ai_enabled);
      })
      .catch((e) => { if (mounted) setErr(formatApiError(e)); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, []);

  const save = async () => {
    setBusy(true);
    setErr("");
    try {
      const updated = await adminSettings.update({
        refresh_interval_minutes: refreshInterval,
        ai_enabled: aiEnabled,
      });
      setData(updated);
      toast.success("Settings updated");
      await onSaved?.();
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <p className="text-slate-400 text-sm">Loading…</p>;

  return (
    <div className="space-y-6 max-w-2xl" data-testid="settings-tab">
      {/* Refresh interval */}
      <AdminCard>
        <div className="p-6">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">News refresh interval</p>
          <h3 className="text-xl font-semibold text-slate-900 mt-2 mb-1">
            {refreshInterval} minute{refreshInterval > 1 ? "s" : ""}
          </h3>
          <p className="text-sm text-slate-500 mb-5">
            Controls how often the news feed cache is refreshed across all card pages.
          </p>
          <div className="grid grid-cols-5 gap-2">
            {[5, 15, 30, 60, 120].map((m) => (
              <button
                key={m}
                onClick={() => setRefreshInterval(m)}
                className={`py-2.5 text-xs font-semibold rounded-lg border transition-colors ${
                  refreshInterval === m
                    ? "bg-indigo-600 border-indigo-600 text-white"
                    : "border-slate-300 text-slate-600 hover:bg-slate-50 hover:border-slate-400"
                }`}
                data-testid={`refresh-interval-${m}`}
              >
                {m < 60 ? `${m}m` : `${m / 60}h`}
              </button>
            ))}
          </div>
        </div>
      </AdminCard>

      {/* AI master switch */}
      <AdminCard>
        <div className="p-6 flex items-start justify-between gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">AI master switch</p>
            <h3 className="text-xl font-semibold text-slate-900 mt-2 mb-1">
              AI generation is {aiEnabled ? "enabled" : "disabled"}
            </h3>
            <p className="text-sm text-slate-500">
              When off, all script and image generation endpoints return a friendly "disabled" message.
            </p>
          </div>
          <AdminToggle
            enabled={aiEnabled}
            onToggle={() => setAiEnabled((v) => !v)}
            size={48}
            data-testid="ai-toggle"
            aria-label="Toggle AI"
          />
        </div>
      </AdminCard>

      {err && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">{err}</div>
      )}

      <AdminPrimaryButton
        busy={busy}
        onClick={save}
        icon={<Gear size={14} weight="bold" />}
        data-testid="settings-save-button"
      >
        Save settings
      </AdminPrimaryButton>
    </div>
  );
}
